import type { ChapterData, ProjectData } from '../../../shared/models';

/**
 * Put an edited chapter back into project.json.
 *
 * A chapter is not a file. It lives inside `project.json > chapters[]`, so
 * every chapter edit — whether it came from the chapter form or from the raw
 * JSON editor — is really a project.json edit, and has to be queued against
 * `/projects/<slug>/project.json`. The JSON editor never did that (DEV-657):
 * it queued the synthetic `chapter:<slug>` selection key instead, so the edit
 * reached no document at all and, because that key has no project slug, could
 * fail the whole save batch it sat in.
 *
 * Both handlers now route through here. Two copies of "put this chapter back"
 * would drift, and every divergence between them loses an edit.
 */
export type ChapterEditOutcome =
	| { ok: true; project: ProjectData }
	/** The edited document is not a JSON object — nothing to splice. */
	| { ok: false; reason: 'not-a-chapter' }
	/** The selected chapter is no longer in project.json (deleted, or renamed elsewhere). */
	| { ok: false; reason: 'chapter-gone' }
	/** The edit renames the chapter — refused here, see below. */
	| { ok: false; reason: 'slug-changed' };

/** Why a chapter edit was not applied. Every one of these is reported to the user. */
export type ChapterEditRejection = Extract<ChapterEditOutcome, { ok: false }>['reason'];

/**
 * `slug-changed` is refused rather than applied, and that refusal is the one
 * place this function deliberately does NOT preserve what the user typed.
 *
 * A chapter slug is a join key: the scene and image documents are stored under
 * it (`scenes.chapterSlug`), and the read-path self-heal drops every ref whose
 * document it cannot find. Rewriting the slug in project.json alone would
 * therefore delete the chapter's entire contents on the next load — silently,
 * which is precisely the failure mode this ticket exists to remove. Renaming
 * has its own endpoint (`ProjectsService.renameSlug`), which moves the
 * documents too; the caller surfaces a message pointing at it.
 */
export function applyChapterEdit(
	project: ProjectData,
	chapterSlug: string,
	edited: unknown,
): ChapterEditOutcome {
	if (!edited || typeof edited !== 'object' || Array.isArray(edited)) return { ok: false, reason: 'not-a-chapter' };

	const chapters = project.chapters ?? [];
	const idx = chapters.findIndex(c => c.slug === chapterSlug);
	if (idx < 0) return { ok: false, reason: 'chapter-gone' };

	if ((edited as ChapterData).slug !== chapterSlug) return { ok: false, reason: 'slug-changed' };

	// Shallow-copy down to the one slot being replaced. The chapter object
	// itself is swapped wholesale, so nothing under the untouched siblings is
	// aliased into a mutation — and a deep clone of a project.json (corner-pin
	// arrays run to thousands of points) on every keystroke is not free.
	const nextChapters = [...chapters];
	nextChapters[idx] = edited as ChapterData;
	return { ok: true, project: { ...project, chapters: nextChapters } };
}

/** Human-readable reason a chapter edit was not applied, for the editor's toast. */
export function chapterEditRejectionMessage(reason: ChapterEditRejection): string {
	switch (reason) {
		case 'not-a-chapter':
			return 'A chapter must be a JSON object — this edit was not saved.';
		case 'chapter-gone':
			return 'This chapter is no longer part of the project — the edit was not saved.';
		case 'slug-changed':
			return 'A chapter slug cannot be changed here: its scenes and images are stored under it. Use Rename in the chapter form. The rest of this edit was not saved either.';
	}
}
